import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectMailbox } from 'selectors/mailboxes_base'
import { selectFolders } from 'ducks/folders/selectors/folders'
import { doSendTestEmail } from 'ducks/mailboxes/actions'
import { doFetchTicketSearch } from 'actions/search'
import { SEND_TEST_EMAIL_RECEIVED } from 'ducks/mailboxes/actionTypes'
import { getRawId } from 'util/globalId'

import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { styles } from '../styles'

const TIMEOUT = 4000

const TestMessageButton = ({
  size,
  type,
  text,
  mailboxId,
  onMessageReceived,
}) => {
  const dispatch = useDispatch()
  const mailbox = useSelector(state => selectMailbox(state, mailboxId))
  const disabled =
    ['sending', 'sent'].includes(mailbox?.testEmailStatus) ||
    ['demo', 'unconfirmed', 'disconnected'].includes(mailbox?.state)
  const timeoutRef = useRef(null)

  const unassignedFolder = useSelector(state => selectFolders(state)).find(
    folder => folder.kind === 'unassigned'
  )
  const unassignedFolderId = getRawId(unassignedFolder?.id)

  const checkIfTestMessageReceived = useCallback(
    async () => {
      const { tickets } = await dispatch(
        doFetchTicketSearch(
          `inbox:${mailbox.id} folder:${unassignedFolderId}`,
          { mailbox: [mailbox.id], folder: [unassignedFolderId] },
          { folder: unassignedFolderId, mailbox: mailbox.id },
          'newest'
        )
      )

      if (tickets) {
        const ticket = tickets.find(
          t =>
            t.state === 'unread' && t.title === "Let's send your first message!"
        )

        if (ticket) {
          await dispatch({
            type: SEND_TEST_EMAIL_RECEIVED,
            payload: { id: mailbox.id },
          })

          clearTimeout(timeoutRef.current)

          if (onMessageReceived) {
            onMessageReceived({
              mailboxId: mailbox.id,
              folderId: unassignedFolderId,
            })
          }

          return
        }
      }

      timeoutRef.current = setTimeout(() => {
        checkIfTestMessageReceived()
      }, TIMEOUT)
    },
    [dispatch, mailbox, unassignedFolderId, onMessageReceived]
  )

  const onSendTestEmailClick = useCallback(
    async () => {
      await dispatch(doSendTestEmail(mailbox.id))

      timeoutRef.current = setTimeout(() => {
        checkIfTestMessageReceived()
      }, TIMEOUT)
    },
    [dispatch, mailbox, checkIfTestMessageReceived]
  )

  useEffect(
    () => {
      if (mailbox.testEmailStatus === 'sent' && !timeoutRef.current) {
        checkIfTestMessageReceived()
      }
    },
    [mailbox, checkIfTestMessageReceived]
  )

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  if (!mailbox) {
    return null
  }

  return (
    <Button
      className="grui mt-12"
      css={styles.button}
      size={size}
      type={type}
      disabled={disabled}
      onClick={onSendTestEmailClick}
    >
      {mailbox.testEmailStatus === undefined && text}
      {mailbox.testEmailStatus === 'failed' && 'Sending a test email failed'}
      {['sending', 'sent'].includes(mailbox.testEmailStatus) && 'Sending...'}
    </Button>
  )
}

export default TestMessageButton
